import { CPMfeEvent, EventMfeRequest, EventMfeResponse } from '@servicetitan/cp-mfe/dist/events';
import {
    PortalStore,
    PortalMode,
    PortalUser,
    PortalStoreEvent,
    PortalUrls,
    getTimezoneCode,
    PortalStoreEventNotify,
    defaultCulture,
} from '@servicetitan/cp-ui';
import { injectable } from '@servicetitan/react-ioc';
import { IPublicPlatformSettings } from 'api';
import { action, makeObservable, observable } from 'mobx';
import { generatePath, To } from 'react-router-dom';
import { generatePathWithQuery } from '../utils/url';

export interface IWebPortalStore extends PortalStore {
    cpMfeResponse: <TOUT>(event: string, data: TOUT, id: number) => void;
}

@injectable()
export class WebPortalStore implements IWebPortalStore {
    @observable mode: PortalMode = {
        view: 'mobile',
        mobile: true,
        tablet: false,
        desktop: false,
        desktopNarrow: false,
        desktopWide: false,
        sidebarMenu: false,
        touch: false,
    };

    @observable user: PortalUser | undefined;
    @observable notifications: { key: string; data: PortalStoreEventNotify }[] = [];

    readonly settings: PortalStore['settings'];
    readonly culture = defaultCulture();

    private navigator?: (path: To) => void;

    constructor() {
        makeObservable(this);

        const portalSettings: IPublicPlatformSettings | undefined = (window as any).PortalSettings;

        const ph = portalSettings?.phoneNumber?.replace(/\D/g, '');
        const phoneNumberFormatted = ph
            ? `(${ph.substring(0, 3)}) ${ph.substring(3, 6)}-${ph.substring(6)}`
            : '';

        /**
         * currently we store tenant timezoneOffset, but it is not correct because offset changes over the year (daylight)
         * sometime we will change it and get ianaTimezoneCode from db, but now we have to calculate it manually
         */
        const timezoneCode = getTimezoneCode(portalSettings?.timezoneOffsetMinutes);

        this.settings = portalSettings
            ? {
                  title: portalSettings.title ?? '',
                  host: portalSettings.host ?? '',
                  email: portalSettings.email ?? '',
                  phoneNumber: portalSettings.phoneNumber ?? '',
                  phoneNumberFormatted,
                  logo: portalSettings.logo ?? '',
                  timezoneCode,
                  bookingEnabled: portalSettings.bookingEnabled ?? false,
              }
            : undefined;
    }

    init() {
        window.addEventListener(EventMfeRequest, this.cpEventListener, false);
    }

    destroy() {
        window.removeEventListener(EventMfeRequest, this.cpEventListener, false);
    }

    @action
    setPortalMode = (mode: PortalMode) => {
        this.mode = mode;
    };

    @action
    setUser = (user: PortalUser['data']) => {
        this.user = new PortalUser(user);
    };

    @action
    removeUser = () => {
        this.user = undefined;
    };

    @action
    hideNotification = (key: string) => {
        this.notifications = this.notifications.filter(not => not.key !== key);
    };

    onNavigate = (navigate?: (path: To) => void) => (this.navigator = navigate);

    event = <K extends keyof PortalStoreEvent>(event: K, data: PortalStoreEvent[K]) => {
        if (event === 'redirect') {
            const [path, params] = data as PortalStoreEvent['redirect'];

            if (path === 'invoice') {
                this.navigate(
                    generatePath(`${PortalUrls.invoices.root}${PortalUrls.invoices.item}`, params)
                );
            } else if (path === 'invoices') {
                this.navigate(
                    generatePathWithQuery(
                        `${PortalUrls.invoices.root}${PortalUrls.invoices.list}`,
                        params
                    )
                );
            } else if (path === 'job') {
                this.navigate(
                    generatePath(`${PortalUrls.jobs.root}${PortalUrls.jobs.item}`, params)
                );
            } else if (path === 'jobs') {
                this.navigate(generatePathWithQuery(`${PortalUrls.jobs.root}`, params));
            } else if (path === 'estimate') {
                this.navigate(
                    generatePath(
                        `${PortalUrls.proposals.root}${PortalUrls.proposals.estimate}`,
                        params as Record<string, any>
                    )
                );
            } else if (path === 'membership') {
                const root = this.user?.isResidential
                    ? PortalUrls.memberships.rootMembership
                    : PortalUrls.memberships.rootContracts;

                this.navigate(
                    generatePath(
                        `${root}${PortalUrls.memberships.item}`,
                        params as Record<string, any>
                    )
                );
            } else if (path === 'service-agreements') {
                this.navigate(generatePath(PortalUrls.serviceAgreements.root));
            } else if (path === 'dashboard') {
                this.navigate(generatePath(''));
            }
        } else if (event === 'notify') {
            const dataDirty = data as PortalStoreEvent['notify'];
            this.showNotification(typeof dataDirty === 'string' ? { title: dataDirty } : dataDirty);
        }
    };

    navigate = (to: To) => {
        this.navigator?.(to);
    };

    cpMfeResponse = <TOUT>(event: string, data: TOUT, id: number) => {
        window.dispatchEvent(
            new CustomEvent<CPMfeEvent<TOUT>>(EventMfeResponse, { detail: { event, data, id } })
        );
    };

    private cpEventListener = (e: CustomEvent<CPMfeEvent<any>>) => {
        if (e.detail?.event && e.detail?.data) {
            this.event(e.detail.event as keyof PortalStoreEvent, e.detail?.data);
        }
    };

    @action private showNotification(data: Partial<PortalStoreEventNotify>) {
        this.notifications.push({
            key: `n${Date.now()}`,
            data: {
                title: '',
                description: '',
                status: 'success',
                ...data,
            },
        });
    }
}
